
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { productsData } from '../../mocks/products';
import { getAverageRating } from '../../mocks/reviews';

const ComparePage: React.FC = () => {
  const [selectedProducts, setSelectedProducts] = useState<number[]>([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightWinner, setHighlightWinner] = useState(true);
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);

  // Load previously selected products from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('compareProducts');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSelectedProducts(parsed);
        }
      } catch (e) {
        console.error('Failed to parse compareProducts from localStorage', e);
      }
    }
  }, []);

  // Persist selected products to localStorage
  useEffect(() => {
    localStorage.setItem('compareProducts', JSON.stringify(selectedProducts));
  }, [selectedProducts]);

  const addProduct = (id: number) => {
    if (selectedProducts.length < 4 && !selectedProducts.includes(id)) {
      setSelectedProducts([...selectedProducts, id]);
    }
    setShowProductSelector(false);
    setSearchQuery('');
  };

  const removeProduct = (id: number) => {
    setSelectedProducts(selectedProducts.filter(p => p !== id));
  };

  const clearAll = () => {
    setSelectedProducts([]);
  };

  const getProduct = (id: number) => productsData.find(p => p.id === id);

  const filteredProducts = productsData.filter(p =>
    !selectedProducts.includes(p.id) &&
    (p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const getLowestPrice = (product: typeof productsData[0]) => {
    return Math.min(...product.retailers.map(r => r.price));
  };

  const getBestPriceProductId = () => {
    if (selectedProducts.length === 0) return null;
    let bestId = selectedProducts[0];
    let bestPrice = getLowestPrice(getProduct(selectedProducts[0])!);

    selectedProducts.forEach(id => {
      const price = getLowestPrice(getProduct(id)!);
      if (price < bestPrice) {
        bestPrice = price;
        bestId = id;
      }
    });
    return bestId;
  };

  const getBestRatingProductId = () => {
    if (selectedProducts.length === 0) return null;
    let bestId = selectedProducts[0];
    let bestRating = getAverageRating(selectedProducts[0]);

    selectedProducts.forEach(id => {
      const rating = getAverageRating(id);
      if (rating > bestRating) {
        bestRating = rating;
        bestId = id;
      }
    });
    return bestId;
  };

  const getBestDiscountProductId = () => {
    if (selectedProducts.length === 0) return null;
    let bestId = selectedProducts[0];
    let bestDiscount = Math.abs(getProduct(selectedProducts[0])?.priceChange || 0);

    selectedProducts.forEach(id => {
      const discount = Math.abs(getProduct(id)?.priceChange || 0);
      if (discount > bestDiscount) {
        bestDiscount = discount;
        bestId = id;
      }
    });
    return bestId;
  };

  const renderStars = (rating: number) => (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(star => (
        <i
          key={star}
          className={`text-xs ${star <= Math.round(rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'}`}
        />
      ))}
    </div>
  );

  const allSpecs = selectedProducts.length > 0
    ? [...new Set(selectedProducts.flatMap(id => getProduct(id)?.specs.map(s => s.label) || []))]
    : [];

  const bestPriceId = getBestPriceProductId();
  const bestRatingId = getBestRatingProductId();
  const bestDiscountId = getBestDiscountProductId();

  // Calculate overall winner
  const getOverallWinner = () => {
    if (selectedProducts.length < 2) return null;

    const scores: Record<number, number> = {};
    selectedProducts.forEach(id => { scores[id] = 0; });

    // Price score (lower is better)
    const prices = selectedProducts.map(id => ({
      id,
      price: getLowestPrice(getProduct(id)!),
    }));
    prices.sort((a, b) => a.price - b.price);
    prices.forEach((p, i) => {
      scores[p.id] += (selectedProducts.length - i) * 3; // Weight: 3
    });

    // Rating score (higher is better)
    const ratings = selectedProducts.map(id => ({
      id,
      rating: getAverageRating(id),
    }));
    ratings.sort((a, b) => b.rating - a.rating);
    ratings.forEach((r, i) => {
      scores[r.id] += (selectedProducts.length - i) * 2; // Weight: 2
    });

    // Discount score (higher is better)
    const discounts = selectedProducts.map(id => ({
      id,
      discount: Math.abs(getProduct(id)?.priceChange || 0),
    }));
    discounts.sort((a, b) => b.discount - a.discount);
    discounts.forEach((d, i) => {
      scores[d.id] += (selectedProducts.length - i); // Weight: 1
    });

    let winnerId = selectedProducts[0];
    let maxScore = scores[selectedProducts[0]];

    Object.entries(scores).forEach(([id, score]) => {
      if (score > maxScore) {
        maxScore = score;
        winnerId = parseInt(id, 10);
      }
    });

    return winnerId;
  };

  const overallWinnerId = getOverallWinner();

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-r from-teal-50 to-emerald-50 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Compare Products</h1>
          <p className="text-gray-600">Compare up to 4 products side by side to find the best deal</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Toolbar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Comparing{' '}
              <span className="font-semibold text-gray-900">{selectedProducts.length}</span> of 4 products
            </span>
            {selectedProducts.length >= 2 && (
              <div className="flex items-center gap-3 pl-4 border-l border-gray-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={highlightWinner}
                    onChange={e => setHighlightWinner(e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-sm text-gray-600">Highlight winners</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOnlyDifferences}
                    onChange={e => setShowOnlyDifferences(e.target.checked)}
                    className="w-4 h-4 text-teal-600 rounded"
                  />
                  <span className="text-sm text-gray-600">Show differences only</span>
                </label>
              </div>
            )}
          </div>
          <div className="flex items-center gap-3">
            {selectedProducts.length > 0 && (
              <button
                onClick={clearAll}
                className="text-sm text-red-600 hover:text-red-700 cursor-pointer whitespace-nowrap"
              >
                Clear All
              </button>
            )}
            <button
              onClick={() => setShowProductSelector(true)}
              disabled={selectedProducts.length >= 4}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" />
              Add Product
            </button>
          </div>
        </div>

        {/* Overall Winner Banner */}
        {selectedProducts.length >= 2 && overallWinnerId && highlightWinner && (
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full flex items-center justify-center">
                <i className="ri-trophy-fill text-white text-xl" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-amber-700 font-medium">Overall Best Choice</p>
                <p className="text-lg font-bold text-gray-900">{getProduct(overallWinnerId)?.name}</p>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="text-gray-500">Best Price</p>
                  <p className="font-semibold text-teal-600">
                    ${getLowestPrice(getProduct(overallWinnerId)!)}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">Rating</p>
                  <p className="font-semibold text-amber-600">
                    {getAverageRating(overallWinnerId).toFixed(1)} ★
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-gray-500">Discount</p>
                  <p className="font-semibold text-green-600">
                    {Math.abs(getProduct(overallWinnerId)?.priceChange || 0)}% off
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {selectedProducts.length === 0 ? (
          // Empty State
          <div className="bg-white rounded-lg shadow-sm p-16 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-scales-3-line text-4xl text-gray-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No products to compare</h2>
            <p className="text-gray-600 mb-6">Add products to start comparing prices and features</p>
            <button
              onClick={() => setShowProductSelector(true)}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
            >
              Add Your First Product
            </button>
          </div>
        ) : (
          // Comparison Table
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Product Headers */}
            <div
              className="grid border-b"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r" />
              {selectedProducts.map(id => {
                const product = getProduct(id);
                if (!product) return null;
                const isWinner = highlightWinner && id === overallWinnerId;
                return (
                  <div
                    key={id}
                    className={`p-4 text-center relative border-r last:border-r-0 ${isWinner ? 'bg-amber-50' : ''}`}
                  >
                    {isWinner && (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-gradient-to-r from-amber-400 to-yellow-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <i className="ri-trophy-fill" />
                        Best
                      </div>
                    )}
                    <button
                      onClick={() => removeProduct(id)}
                      className="absolute top-2 right-2 w-8 h-8 bg-gray-100 hover:bg-red-100 rounded-full flex items-center justify-center cursor-pointer transition-colors"
                    >
                      <i className="ri-close-line text-gray-500 hover:text-red-500" />
                    </button>
                    <div className="w-32 h-32 mx-auto mb-3 bg-gray-50 rounded-lg overflow-hidden">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <Link to={`/product/${id}`} className="font-semibold text-gray-900 hover:text-teal-600 cursor-pointer">
                      {product.name}
                    </Link>
                    <p className="text-xs text-gray-500 mt-1">{product.brand}</p>
                  </div>
                );
              })}
            </div>

            {/* Quick Stats Row */}
            <div
              className="grid border-b bg-gray-50"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div
                className="p-3 border-r font-semibold text-gray-800 text-sm uppercase tracking-wide"
                style={{ gridColumn: `span ${selectedProducts.length + 1}` }}
              >
                Quick Comparison
              </div>
            </div>

            {/* Price Row */}
            <div
              className="grid border-b"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 flex items-center">
                <i className="ri-price-tag-3-line mr-2 text-teal-600" />
                Best Price
              </div>
              {selectedProducts.map(id => {
                const product = getProduct(id);
                if (!product) return null;
                const lowestPrice = getLowestPrice(product);
                const lowestRetailer = product.retailers.find(r => r.price === lowestPrice);
                const isBest = highlightWinner && id === bestPriceId;
                return (
                  <div
                    key={id}
                    className={`p-4 text-center border-r last:border-r-0 ${isBest ? 'bg-green-50' : ''}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span className={`text-2xl font-bold ${isBest ? 'text-green-600' : 'text-teal-600'}`}>
                        ${lowestPrice}
                      </span>
                      {isBest && <i className="ri-medal-fill text-green-500" />}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">at {lowestRetailer?.name}</p>
                  </div>
                );
              })}
            </div>

            {/* Rating Row */}
            <div
              className="grid border-b"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 flex items-center">
                <i className="ri-star-line mr-2 text-teal-600" />
                Rating
              </div>
              {selectedProducts.map(id => {
                const rating = getAverageRating(id);
                const isBest = highlightWinner && id === bestRatingId;
                return (
                  <div
                    key={id}
                    className={`p-4 text-center border-r last:border-r-0 flex flex-col items-center justify-center ${
                      isBest ? 'bg-amber-50' : ''
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {renderStars(rating)}
                      {isBest && <i className="ri-medal-fill text-amber-500" />}
                    </div>
                    <span
                      className={`text-sm mt-1 ${isBest ? 'text-amber-600 font-semibold' : 'text-gray-600'}`}
                    >
                      {rating > 0 ? rating.toFixed(1) : 'No reviews'}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Discount Row */}
            <div
              className="grid border-b"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 flex items-center">
                <i className="ri-percent-line mr-2 text-teal-600" />
                Current Discount
              </div>
              {selectedProducts.map(id => {
                const product = getProduct(id);
                if (!product) return null;
                const discount = Math.abs(product.priceChange || 0);
                const isBest = highlightWinner && id === bestDiscountId;
                const isPositive = product.priceChange && product.priceChange > 0;
                return (
                  <div
                    key={id}
                    className={`p-4 text-center border-r last:border-r-0 ${isBest ? 'bg-green-50' : ''}`}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <span
                        className={`text-xl font-bold ${
                          isBest
                            ? 'text-green-600'
                            : isPositive
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}
                      >
                        {isPositive ? '↑' : '↓'} {discount}%
                      </span>
                      {isBest && <i className="ri-medal-fill text-green-500" />}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Price Range Row */}
            {!showOnlyDifferences && (
              <div
                className="grid border-b"
                style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
              >
                <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 flex items-center">
                  <i className="ri-line-chart-line mr-2 text-teal-600" />
                  Price Range
                </div>
                {selectedProducts.map(id => {
                  const product = getProduct(id);
                  if (!product) return null;
                  const rangePercent =
                    product.highestPrice !== product.lowestPrice
                      ? ((getLowestPrice(product) - product.lowestPrice) /
                          (product.highestPrice - product.lowestPrice)) *
                        100
                      : 0;
                  return (
                    <div key={id} className="p-4 text-center border-r last:border-r-0">
                      <span className="text-sm text-gray-900">
                        ${product.lowestPrice} - ${product.highestPrice}
                      </span>
                      <div className="mt-2 h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-full bg-gradient-to-r from-green-400 to-teal-500 rounded-full"
                          style={{ width: `${rangePercent}%` }}
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Current position in range</p>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Category Row */}
            {!showOnlyDifferences && (
              <div
                className="grid border-b"
                style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
              >
                <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 flex items-center">
                  <i className="ri-folder-line mr-2 text-teal-600" />
                  Category
                </div>
                {selectedProducts.map(id => {
                  const product = getProduct(id);
                  return (
                    <div key={id} className="p-4 text-center border-r last:border-r-0">
                      <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                        {product?.category}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Specs Section */}
            {allSpecs.length > 0 && (
              <>
                <div
                  className="grid"
                  style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
                >
                  <div
                    className="p-3 bg-gray-100 border-r font-semibold text-gray-800 text-sm uppercase tracking-wide"
                    style={{ gridColumn: `span ${selectedProducts.length + 1}` }}
                  >
                    Specifications
                  </div>
                </div>
                {allSpecs.map(spec => {
                  const values = selectedProducts.map(
                    id => getProduct(id)?.specs.find(s => s.label === spec)?.value || '—'
                  );
                  const allSame = values.every(v => v === values[0]);

                  if (showOnlyDifferences && allSame) return null;

                  return (
                    <div
                      key={spec}
                      className="grid border-b"
                      style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
                    >
                      <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 text-sm">{spec}</div>
                      {selectedProducts.map(id => {
                        const product = getProduct(id);
                        const specValue = product?.specs.find(s => s.label === spec)?.value;
                        return (
                          <div
                            key={id}
                            className={`p-4 text-center border-r last:border-r-0 ${
                              !allSame && specValue ? 'bg-teal-50' : ''
                            }`}
                          >
                            <span className="text-sm text-gray-900">{specValue || '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </>
            )}

            {/* Retailers Section */}
            <div
              className="grid"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div
                className="p-3 bg-gray-100 border-r font-semibold text-gray-800 text-sm uppercase tracking-wide"
                style={{ gridColumn: `span ${selectedProducts.length + 1}` }}
              >
                Available Retailers
              </div>
            </div>
            <div
              className="grid"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r font-medium text-gray-700 text-sm">Where to Buy</div>
              {selectedProducts.map(id => {
                const product = getProduct(id);
                return (
                  <div key={id} className="p-4 border-r last:border-r-0">
                    <div className="space-y-2">
                      {product?.retailers.slice(0, 4).map((retailer, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between text-sm p-2 rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                              <i className={`${retailer.logo} text-xs text-gray-600`} />
                            </div>
                            <span className="text-gray-700">{retailer.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-medium ${
                                retailer.price === getLowestPrice(product!)
                                  ? 'text-green-600'
                                  : 'text-gray-900'
                              }`}
                            >
                              ${retailer.price}
                            </span>
                            {retailer.price === getLowestPrice(product!) && (
                              <span className="px-1.5 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                Best
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Row */}
            <div
              className="grid border-t"
              style={{ gridTemplateColumns: `200px repeat(${selectedProducts.length}, 1fr)` }}
            >
              <div className="p-4 bg-gray-50 border-r" />
              {selectedProducts.map(id => {
                const product = getProduct(id);
                if (!product) return null;
                return (
                  <div key={id} className="p-4 text-center border-r last:border-r-0">
                    <Link
                      to={`/product/${id}`}
                      className="inline-block px-6 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 cursor-pointer whitespace-nowrap"
                    >
                      View Details
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Quick Add Suggestions */}
        {selectedProducts.length > 0 && selectedProducts.length < 4 && (
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add more products to compare</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {productsData
                .filter(p => !selectedProducts.includes(p.id))
                .slice(0, 4)
                .map(product => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product.id)}
                    className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:border-teal-500 hover:shadow-md transition-all cursor-pointer text-left"
                  >
                    <div className="w-full h-24 bg-gray-50 rounded-lg overflow-hidden mb-3">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                    <p className="text-xs text-gray-500">{product.brand}</p>
                    <p className="text-sm font-semibold text-teal-600 mt-1">
                      ${getLowestPrice(product)}
                    </p>
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowProductSelector(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-xl shadow-2xl z-50 max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Add Product to Compare</h3>
                <button
                  onClick={() => setShowProductSelector(false)}
                  className="p-2 hover:bg-gray-100 rounded-full cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600" />
                </button>
              </div>
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-96 p-4">
              <div className="grid grid-cols-2 gap-4">
                {filteredProducts.map(product => (
                  <button
                    key={product.id}
                    onClick={() => addProduct(product.id)}
                    className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg hover:border-teal-500 hover:bg-teal-50 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-16 h-16 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={product.image} alt={product.name} className="w-full h-full object-contain" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{product.name}</p>
                      <p className="text-xs text-gray-5">{product.brand}</p>
                      <p className="text-sm font-semibold text-teal-600 mt-1">
                        ${getLowestPrice(product)}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
              {filteredProducts.length === 0 && (
                <div className="text-center py-8 text-gray-500">No products found</div>
              )}
            </div>
          </div>
        </>
      )}

      <Footer />
    </div>
  );
};

export default ComparePage;
